import { Routes, Route, Navigate, Link, useLocation } from 'react-router';
import { useState } from 'react';
import {
  Activity,
  Cloud,
  Workflow,
  ChevronRight,
  Menu,
  X,
  BookOpen,
  Terminal,
  Server,
  Code,
  Container,
  Zap,
  LayoutDashboard,
  Bell,
  Shield,
  ShieldCheck,
  Database,
  Sparkles,
  FileCheck,
  Radio,
  Search,
  Brain,
  FlaskConical,
  KeyRound,
  Network,
  Plug,
} from 'lucide-react';
import { LogPulseLogo } from '@/components/LogPulseLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';
import { LpqlSyntaxPage } from './LpqlSyntaxPage';
import { AnomalyDetectionPage } from './AnomalyDetectionPage';
import { BuildingPipelinesPage } from './BuildingPipelinesPage';
import { CloudSetupPage } from './CloudSetupPage';
import { AgentsFleetPage } from './AgentsFleetPage';
import { HttpApiPage } from './HttpApiPage';
import { KubernetesPage } from './KubernetesPage';
import { QuickstartPage } from './QuickstartPage';
import { DashboardPage } from './DashboardPage';
import { AlertingPage } from './AlertingPage';
import { AuthenticationPage } from './AuthenticationPage';
import { DataRetentionPage } from './DataRetentionPage';
import { AiQueryGeneratorPage } from './AiQueryGeneratorPage';
import { PlatformLimitsPage } from './PlatformLimitsPage';
import { CompliancePage } from './CompliancePage';
import { OtlpPage } from './OtlpPage';
import { AiLoggingGuidePage } from './AiLoggingGuidePage';
import { AiEvaluationsPage } from './AiEvaluationsPage';
import { PersonalAccessTokensPage } from './PersonalAccessTokensPage';
import { SecurityMonitoringPage } from './SecurityMonitoringPage';
import { ServiceIntelligencePage } from './ServiceIntelligencePage';
import { McpServerPage } from './McpServerPage';
import { ParsersDocsPage } from './ParsersDocsPage';

interface NavSection {
  title: string;
  items: NavItem[];
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: boolean;
}

function useNavSections(): NavSection[] {
  const t = useT();
  return [
    {
      title: 'Getting Started',
      items: [
        { label: 'Quickstart', href: '/docs/getting-started/quickstart', icon: Zap },
      ],
    },
    {
      title: t('docs.nav.search'),
      items: [
        { label: t('docs.nav.lpqlSyntax'), href: '/docs/search/lpql-syntax', icon: Terminal },
        { label: 'AI Query Generator', href: '/docs/search/ai-query-generator', icon: Sparkles },
        { label: 'AI Investigator', href: '/docs/search/ai-investigator', icon: Brain, soon: true },
      ],
    },
    {
      title: t('docs.nav.anomalyDetection'),
      items: [
        { label: t('docs.nav.anomalyOverview'), href: '/docs/anomaly-detection/overview', icon: Activity },
        { label: 'Alerting & Notifications', href: '/docs/anomaly-detection/alerting', icon: Bell },
      ],
    },
    {
      title: 'Observability',
      items: [
        { label: 'Service Intelligence', href: '/docs/observability/service-intelligence', icon: Network },
      ],
    },
    {
      title: 'Security',
      items: [
        { label: 'Security Monitoring (SIEM)', href: '/docs/security/monitoring', icon: ShieldCheck },
      ],
    },
    {
      title: t('docs.nav.integrations'),
      items: [
        { label: t('docs.nav.cloudSetup'), href: '/docs/integrations/cloud-setup', icon: Cloud },
        { label: 'MCP Server', href: '/docs/integrations/mcp-server', icon: Plug },
        { label: t('docs.nav.httpApi'), href: '/docs/integrations/http-api', icon: Code },
        { label: t('docs.nav.kubernetes'), href: '/docs/integrations/kubernetes', icon: Container },
        { label: t('docs.nav.otlp'), href: '/docs/integrations/otlp', icon: Radio },
        { label: 'AI Logging Guide', href: '/docs/integrations/ai-logging-guide', icon: Sparkles },
      ],
    },
    {
      // Mirrors Configure → Data processing in the app: collect, parse, route.
      title: t('docs.nav.dataProcessing'),
      items: [
        { label: t('docs.nav.agents'), href: '/docs/integrations/agents', icon: Server },
        { label: t('docs.nav.parsers'), href: '/docs/data-processing/parsers', icon: Search },
        { label: t('docs.nav.buildingPipelines'), href: '/docs/pipelines/building-pipelines', icon: Workflow },
      ],
    },
    {
      title: 'API',
      items: [
        { label: 'AI Evaluations', href: '/docs/api/ai-evaluations', icon: FlaskConical },
        { label: 'Personal Access Tokens', href: '/docs/api/personal-access-tokens', icon: KeyRound },
      ],
    },
    {
      title: 'Platform',
      items: [
        { label: 'Dashboard & UI', href: '/docs/platform/dashboard', icon: LayoutDashboard },
        { label: 'Authentication & Teams', href: '/docs/platform/authentication', icon: Shield },
        { label: 'Data Models', href: '/docs/platform/data-models', icon: Database, soon: true },
        { label: 'Data Retention & Storage', href: '/docs/platform/data-retention', icon: Database },
        { label: 'Platform Limits & Quotas', href: '/docs/platform/limits', icon: Activity },
        { label: 'Compliance & Trust', href: '/docs/platform/compliance', icon: FileCheck },
      ],
    },
  ];
}

function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const sections = useNavSections();

  return (
    <nav className="space-y-6">
      {sections.map((section) => (
        <div key={section.title}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-3">
            {section.title}
          </h4>
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.href;
              return (
                <li key={item.href}>
                  {item.soon ? (
                    <span className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 dark:text-gray-600 cursor-not-allowed">
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                      <span className="ml-auto text-[10px] font-medium uppercase tracking-wider bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 px-1.5 py-0.5 rounded">
                        Soon
                      </span>
                    </span>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                        active
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function DocsHeader() {
  const t = useT();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-40 glass border-b border-gray-200/60 dark:border-gray-800/60">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Open docs navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <LogPulseLogo className="h-6 w-6" />
                <span className="font-bold text-gray-900 dark:text-white">LogPulse</span>
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600 hidden sm:block" />
              <Link
                to="/docs"
                className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                {t('docs.title')}
              </Link>
            </div>

            {/* Right */}
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
              <a
                href="https://app.logpulse.io/sign-up"
                className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                {t('nav.getStarted')}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {mobileNavOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="fixed top-0 left-0 bottom-0 w-72 bg-white dark:bg-gray-950 z-50 shadow-2xl overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-6">
              <span className="font-bold text-gray-900 dark:text-white">{t('docs.title')}</span>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <DocsSidebar onNavigate={() => setMobileNavOpen(false)} />
          </div>
        </>
      )}
    </>
  );
}

export default function DocsLayout() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <DocsHeader />
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 py-8">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-[4.5rem] overflow-y-auto max-h-[calc(100vh-5rem)] pb-8">
              <DocsSidebar />
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            <Routes>
              <Route index element={<Navigate to="/docs/getting-started/quickstart" replace />} />
              <Route path="getting-started/quickstart" element={<QuickstartPage />} />
              <Route path="search/lpql-syntax" element={<LpqlSyntaxPage />} />
              <Route path="search/ai-query-generator" element={<AiQueryGeneratorPage />} />
              <Route path="anomaly-detection/overview" element={<AnomalyDetectionPage />} />
              <Route path="anomaly-detection/alerting" element={<AlertingPage />} />
              <Route path="observability/service-intelligence" element={<ServiceIntelligencePage />} />
              <Route path="security/monitoring" element={<SecurityMonitoringPage />} />
              <Route path="integrations/cloud-setup" element={<CloudSetupPage />} />
              <Route path="integrations/agents" element={<AgentsFleetPage />} />
              {/* Legacy URL, kept so bookmarks to the old Vector Agent
                  page resolve to the rebuilt Agents & fleet docs. */}
              <Route
                path="integrations/vector-agent"
                element={<Navigate to="/docs/integrations/agents" replace />}
              />
              <Route path="integrations/mcp-server" element={<McpServerPage />} />
              <Route path="integrations/http-api" element={<HttpApiPage />} />
              <Route path="integrations/kubernetes" element={<KubernetesPage />} />
              <Route path="integrations/otlp" element={<OtlpPage />} />
              <Route path="integrations/ai-logging-guide" element={<AiLoggingGuidePage />} />
              <Route path="data-processing/parsers" element={<ParsersDocsPage />} />
              <Route path="platform/parser-rules" element={<Navigate to="/docs/data-processing/parsers" replace />} />
              <Route path="pipelines/building-pipelines" element={<BuildingPipelinesPage />} />
              <Route path="api/ai-evaluations" element={<AiEvaluationsPage />} />
              <Route path="api/personal-access-tokens" element={<PersonalAccessTokensPage />} />
              <Route path="platform/dashboard" element={<DashboardPage />} />
              <Route path="platform/authentication" element={<AuthenticationPage />} />
              <Route path="platform/data-retention" element={<DataRetentionPage />} />
              <Route path="platform/limits" element={<PlatformLimitsPage />} />
              <Route path="platform/compliance" element={<CompliancePage />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
